import styles from './renttimer.module.sass';
import cn from 'classnames';
import { FontIcon } from '../FontIcon';

interface RentTimerProps extends React.HTMLAttributes<HTMLDivElement> {
    timeCount: [number, 'hour' | 'minute'];
    /** Optional value to define base time - the 100% from which to start count down */
    base?: number;
    /** String represent the label for hour/minute value */
    timeType: string;
    /** String to show after timer */
    labelRemaining: string;
    /** String to show before timer (for 'inline' variant) */
    labelBefore?: string;
    /** Boolean params to change the visual appearance of the timer */
    expired?: boolean;
    expiredLabel?: string;
    almostEnded?: boolean;
    inline?: boolean;
    /** Change direction of the timer filling  */
    reverse?: boolean;
    isSmall?: boolean;
}

/**
 * Converts a given number into a percentage of a specified base value.
 * @param num - The number to be converted into a percentage.
 * @param base - The base number from which the percentage is calculated.
 * @returns The percentage value of num as a part of base.
 */
function numberToPercent(num: number, base: number): number {
    if (base === 0) throw new Error('Base value cannot be zero.');
    return parseFloat(((num / base) * 100).toFixed(1));
}

export const RentTimer = ({
    timeCount,
    timeType,
    labelRemaining,
    labelBefore,
    expired,
    expiredLabel,
    almostEnded,
    inline,
    base,
    reverse,
    isSmall,
    className,
    ...props
}: RentTimerProps) => {
    const classes = cn(
        styles.timer,
        expired && styles.expired,
        almostEnded && styles.almost,
        inline && styles.inline,
        inline && labelBefore && styles.holder,
        reverse && styles.reverse,
        isSmall && styles.small,
        className
    );

    // const percent = numberToPercent(timeCount[0], timeCount[1] === 'hour' ? 48 : 60);
    const defaultBase = timeCount[1] === 'hour' ? 48 : 60;
    const percent = numberToPercent(timeCount[0], base || defaultBase);

    return (
        <div className={classes} {...props}>
            {inline && labelBefore && <span className={styles.label}>{labelBefore}</span>}
            <div className={styles.frame}>
                <svg viewBox="0 0 36 36" className={styles.progress}>
                    <path
                        className={styles['circle-bg']}
                        d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                        className={styles.circle}
                        strokeDasharray={
                            reverse
                                ? expired
                                    ? '0, 100'
                                    : `${100 - percent}, 100` // start with empty and then fill with color
                                : expired
                                ? '100, 100'
                                : `${percent}, 100` // start with full and then transparent
                        }
                        d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                </svg>
                {expired && labelBefore && (
                    <span className={styles.mark}>
                        <FontIcon name="exclamation" size="medium" md="medium" color="red" />
                    </span>
                )}
            </div>
            <div className={styles.count}>
                {expired &&
                    (labelBefore ? (
                        <strong>
                            <span>{timeCount[0]}</span>
                            {timeType}
                        </strong>
                    ) : (
                        <strong>{expiredLabel}</strong>
                    ))}
                {!expired && (
                    <>
                        <strong>
                            <span>{timeCount[0]}</span>
                            {timeType}
                        </strong>
                        <i>{labelRemaining}</i>
                    </>
                )}
            </div>
        </div>
    );
};
